/**
 * Main-thread handle to the solver worker.
 *
 * Serialises requests: the solve is hundreds of milliseconds, and a user
 * dragging the wind dial would otherwise queue a dozen stale solves. Only the
 * most recent request survives.
 */

import * as Comlink from 'comlink';
import type { SolverApi } from './api';

let worker: Worker | null = null;
let remote: Comlink.Remote<SolverApi> | null = null;

export function getSolver(): Comlink.Remote<SolverApi> {
  if (!remote) {
    worker = new Worker(new URL('./solver.worker.ts', import.meta.url), { type: 'module' });
    remote = Comlink.wrap<SolverApi>(worker);
  }
  return remote;
}

export function disposeSolver(): void {
  worker?.terminate();
  worker = null;
  remote = null;
}

/**
 * Run `task`, coalescing bursts: while one task is in flight, only the newest
 * pending task is kept and everything between it and the running one is dropped.
 */
export function makeCoalescer<T>() {
  let running = false;
  let pending: (() => Promise<T>) | null = null;
  let pendingResolve: ((v: T) => void) | null = null;
  let pendingReject: ((e: unknown) => void) | null = null;

  const drain = async () => {
    if (running || !pending) return;
    running = true;
    const task = pending;
    const resolve = pendingResolve!;
    const reject = pendingReject!;
    pending = null;
    pendingResolve = null;
    pendingReject = null;
    try {
      resolve(await task());
    } catch (e) {
      reject(e);
    } finally {
      running = false;
      void drain();
    }
  };

  return (task: () => Promise<T>): Promise<T> =>
    new Promise<T>((resolve, reject) => {
      // Anything already queued is now stale.
      pendingResolve?.(undefined as T);
      pending = task;
      pendingResolve = resolve;
      pendingReject = reject;
      void drain();
    });
}
